import {
  type CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { OVERLAY_Z, OverlayPortal } from "../utils/OverlayPortal";

/**
 * Pile des volets ouverts, du plus ancien au plus récent.
 *
 * Chaque volet écoute `keydown` sur `window` : sans cette pile, une seule frappe d'Échap fermait
 * **tous** les volets empilés d'un coup — fermer le volet d'historique emportait le volet de partie
 * en dessous, et donc la navigation. Seul le dernier de la pile réagit.
 */
const openShutters: string[] = [];

type ShutterProps = {
  onClose: () => void;
  /** Libellé accessibilité (ex. i18n) pour le voile et le bouton mobile */
  closeLabel: string;
  /** Libellé accessibilité pour la poignée de redimensionnement */
  resizeAriaLabel: string;
  children: ReactNode;
  /**
   * Largeur initiale en % du viewport : nombre entier 1–100 (ex. 75),
   * ou ratio 0–1 exclusif (ex. 0.75 → 75 %).
   */
  initialWidthPercent?: number;
  /**
   * Largeur minimale en % du viewport (redimensionnement inclus). Défaut 50.
   * Même convention que initialWidthPercent (ex. 0.3 → 30 %).
   */
  minWidthPercent?: number;
  /** Classes du conteneur scrollable principal */
  contentClassName?: string;
};

/** 75 ou 0.75 → 75 ; 1 reste 1 (%). */
function toPercent(value: number): number {
  let p = value;
  if (p > 0 && p < 1) p *= 100;
  return Math.min(Math.max(p, 1), 100);
}

function clampPanelWidth(px: number, minPct: number): number {
  if (typeof window === "undefined") return px;
  const w = window.innerWidth;
  const maxPx = w * 0.95;
  let minPx = (w * toPercent(minPct)) / 100;
  if (minPx > maxPx) minPx = maxPx;
  return Math.min(Math.max(px, minPx), maxPx);
}

function defaultWidthPx(minPct: number): number {
  if (typeof window === "undefined") return 560;
  const w = window.innerWidth;
  const raw = Math.min(Math.max(w * 0.65, 320), Math.min(w * 0.95, 896));
  return clampPanelWidth(raw, minPct);
}

function widthFromPercent(percent: number, minPct: number): number {
  if (typeof window === "undefined") return 560;
  const w = window.innerWidth;
  return clampPanelWidth((w * toPercent(percent)) / 100, minPct);
}

const defaultContentClassName = "min-h-0 flex-1 overflow-y-auto";

export function Shutter({
  onClose,
  closeLabel,
  resizeAriaLabel,
  children,
  initialWidthPercent,
  minWidthPercent = 50,
  contentClassName = defaultContentClassName,
}: ShutterProps) {
  const [panelWidth, setPanelWidth] = useState(() =>
    initialWidthPercent != null
      ? widthFromPercent(initialWidthPercent, minWidthPercent)
      : defaultWidthPx(minWidthPercent),
  );
  const dragging = useRef(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      setPanelWidth((current) => clampPanelWidth(current, minWidthPercent));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [minWidthPercent]);

  const shutterId = useId();

  useEffect(() => {
    openShutters.push(shutterId);
    return () => {
      const index = openShutters.indexOf(shutterId);
      if (index >= 0) openShutters.splice(index, 1);
    };
  }, [shutterId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Un volet ouvert par-dessus celui-ci est seul concerné : fermer les deux d'un coup ferait
      // perdre le contexte de travail, et ici la navigation.
      if (openShutters[openShutters.length - 1] !== shutterId) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, shutterId]);

  const stopDrag = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const next = clampPanelWidth(
        window.innerWidth - e.clientX,
        minWidthPercent,
      );
      setPanelWidth(next);
    };
    const onUp = () => stopDrag();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [stopDrag, minWidthPercent]);

  // Monté sous `<body>` : l'animation d'ouverture laisse une `transform` sur le panneau, qui
  // deviendrait le bloc conteneur des calques ouverts depuis l'intérieur du volet — voile tronqué,
  // confirmation à la largeur du volet. L'empilement suit l'ordre d'ouverture, cf. `OVERLAY_Z`.
  return (
    <OverlayPortal>
      <button
        type="button"
        className={`fixed inset-0 ${OVERLAY_Z} animate-shutter-backdrop bg-black/40 dark:bg-black/50`}
        onClick={onClose}
        aria-label={closeLabel}
      />
      <div
        className={`fixed inset-y-0 left-0 right-0 ${OVERLAY_Z} flex min-h-0 min-w-0 animate-shutter-panel flex-col bg-white shadow-xl dark:bg-gray-900 md:left-auto md:right-0 md:w-[var(--shutter-panel-w)] md:border-l md:border-gray-200 dark:md:border-gray-700`}
        role="dialog"
        aria-modal="true"
        style={
          {
            ["--shutter-panel-w" as string]: `${panelWidth}px`,
          } as CSSProperties
        }
      >
        {/* Poignée : desktop uniquement, curseur est–ouest. `z-10` suffit — elle n'a qu'à passer
            au-dessus du contenu de son propre panneau, qui forme un contexte d'empilement. */}
        <div
          aria-label={resizeAriaLabel}
          className="absolute left-0 top-0 z-10 hidden h-full w-3 max-w-[12px] -translate-x-1/2 cursor-ew-resize touch-none border-0 bg-transparent p-0 hover:bg-blue-500/15 dark:hover:bg-blue-400/20 md:block"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dragging.current = true;
            document.body.style.cursor = "ew-resize";
            document.body.style.userSelect = "none";
          }}
        />
        <div className={contentClassName}>{children}</div>
      </div>
    </OverlayPortal>
  );
}
